import {
  MACRO_CRITERIA, MACRO_DIMENSIONS, MACRO_DIMENSION_META, MACRO_PER_CELL, MACRO_QUADRANTS, MACRO_QUADRANT_META,
  macroCell, macroMetrics, macroReady, macroSuggestionsFor,
  type Macro as MacroContent, type MacroDimension, type MacroFactor, type MacroQuadrant,
} from '@ads/shared';
import { useEffect, useMemo, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { useConsolidate, useMacroSuggestions, useRefreshMacroSuggestions, useToolReading } from '../lib/queries';
import { useToolDraft } from '../lib/useToolDraft';
import { ReadingStatus } from './GapsDashboard';

/* ─────────────────────────────────────────────────────────────────────
   Macro Cenário.
   1 · Matriz: forças, fraquezas, oportunidades e ameaças × as seis
       dimensões do PESTAL. Cada célula é um campo — até quatro fatores —,
       e o painel ao lado mostra a célula selecionada (mover de quadrante
       ou de dimensão, descrever com mais espaço) e as sugestões da IA
       para ela.
   2 · Síntese consolidada: inteira da IA, refeita a cada mudança da
       matriz — principais fatores e impacto de cada linha, dimensões
       predominantes (contadas), direcionamentos e a validação final.
   ───────────────────────────────────────────────────────────────────── */

type Tab = 'matriz' | 'sintese';
type Sel = { q: MacroQuadrant; d: MacroDimension; id?: string };

const DIM_ICON: Record<MacroDimension, (p: object) => ReactNode> = {
  politico: Ic.Landmark, economico: Ic.Coins, social: Ic.Users, ambiental: Ic.Leaf, tecnologico: Ic.Chip, legal: Ic.Scale,
};
const newId = () => `mf-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
const focusFactor = (id: string) => requestAnimationFrame(() => {
  const el = document.querySelector<HTMLTextAreaElement>(`[data-mf="${id}"]`);
  el?.focus(); el?.setSelectionRange(el.value.length, el.value.length);
});

export function Macro() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', 'Macro Cenário'], j: .5 });
  const nav = useNavigate();
  const { local, commit, flush, saveState, status } = useToolDraft('macro');
  const reading = useToolReading('macro');
  const sugQ = useMacroSuggestions();
  const refresh = useRefreshMacroSuggestions();
  const consolidate = useConsolidate();
  const [tab, setTab] = useState<Tab>('matriz');
  const [sel, setSel] = useState<Sel | null>(null);

  const c = local;
  const m = useMemo(() => (c ? macroMetrics(c) : null), [c]);
  const r = reading.data;
  const syn = r?.reading ?? null;
  const busy = !!r && (r.pending || r.stale);
  const sug = sugQ.data?.reading ?? null;
  const sugBusy = refresh.isPending || (!!sugQ.data && (sugQ.data.pending || sugQ.data.stale));

  /* o fator selecionado (se ainda existir) e a sua célula */
  const selFactor = sel?.id && c ? c.factors.find(f => f.id === sel.id) ?? null : null;
  const cellFull = (q: MacroQuadrant, d: MacroDimension) => !!c && macroCell(c, q, d).length >= MACRO_PER_CELL;

  /** Sair de um fator deixado em branco o tira da matriz. */
  const dropBlank = (keep?: string) => {
    if (!c) return c;
    const blank = c.factors.filter(f => !f.text.trim() && f.id !== keep);
    if (!blank.length) return c;
    const next = { factors: c.factors.filter(f => f.text.trim() || f.id === keep) };
    commit(next);
    return next;
  };
  const select = (s: Sel) => { if (s.id !== sel?.id) dropBlank(s.id); setSel(s); };
  const patch = (id: string, p: Partial<MacroFactor>) => {
    if (!c) return;
    commit({ factors: c.factors.map(f => (f.id === id ? { ...f, ...p, ai: p.text !== undefined ? false : f.ai } : f)) });
  };
  /** Um fator novo na célula; o foco vai para ele (menos quando se escreve no painel, que fica com o foco). */
  const add = (q: MacroQuadrant, d: MacroDimension, text: string, opts: { ai?: boolean; focus?: boolean } = {}) => {
    if (!c || cellFull(q, d)) return;
    const id = newId();
    commit({ factors: [...c.factors.filter(f => f.text.trim()), { id, quadrant: q, dimension: d, text, ai: opts.ai ?? false }] });
    setSel({ q, d, id });
    if (opts.focus !== false) focusFactor(id);
  };
  const remove = (id: string) => {
    if (!c) return;
    commit({ factors: c.factors.filter(f => f.id !== id) });
    if (sel?.id === id) setSel({ q: sel.q, d: sel.d });
  };
  /** Mover o fator (ou a seleção vazia) para outro quadrante ou dimensão. */
  const move = (p: { q?: MacroQuadrant; d?: MacroDimension }) => {
    if (!sel) return;
    const q = p.q ?? sel.q, d = p.d ?? sel.d;
    if (selFactor) {
      if (cellFull(q, d)) return;
      patch(selFactor.id!, { quadrant: q, dimension: d });
    }
    setSel({ ...sel, q, d });
  };

  const go = (t: Tab) => { dropBlank(); flush(); setTab(t); document.querySelector('.stage')?.scrollTo({ top: 0, behavior: 'smooth' }); };
  const finish = () => { dropBlank(); flush(); consolidate.mutate(undefined, { onSettled: () => nav('/strategy/horizontes') }); };

  return (
    <section className="view on" data-v="macro">
      <button className="back" type="button" onClick={() => { flush(); nav('/strategy'); }}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 08 / 10</b></div>
          <h1>Macro Cenário</h1>
          <p className="lede">{tab === 'sintese'
            ? 'Os principais fatores, as dimensões predominantes e o impacto estratégico para a organização — consolidados pela IA a partir da matriz.'
            : 'Mapeie os fatores políticos, econômicos, sociais, ambientais, tecnológicos e legais que impactam a estratégia da organização.'}</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
        </div>
      </div>

      <div className="bmc-top">
        <ol className="stepper" aria-label="Etapas">
          {([['matriz', 'Matriz'], ['sintese', 'Síntese consolidada']] as const).map(([k, label], i) => (
            <li key={k} className={tab === k ? 'on' : tab === 'sintese' && k === 'matriz' ? 'done' : ''}>
              <button type="button" onClick={() => go(k)} aria-current={tab === k ? 'step' : undefined}>
                <span className="n">{tab === 'sintese' && k === 'matriz' ? <Ic.Check /> : i + 1}</span>{label}
              </button>
            </li>
          ))}
        </ol>
        <div className="bmc-align" title="Da síntese consolidada: o quanto a leitura do cenário sustenta o propósito e o objetivo central.">
          <span>Alinhamento ao propósito</span>
          <span className={`vi-bar${busy ? ' is-dim' : ''}`}><i style={{ '--v': `${syn?.alinhamento ?? 0}%` } as CSSProperties} /></span>
          <b>{syn ? `${syn.alinhamento}%` : '—'}</b>
        </div>
      </div>

      {!c || !m ? <div className="loading-line" /> : tab === 'matriz' ? (
        <div className="mc-grid">
          <section className="card mc-card">
            <div className="mc-h">
              <h3 className="in-h3">Matriz de Macro Cenário</h3>
              <p className="subhead">Clique numa célula e escreva o fator — até {MACRO_PER_CELL} por célula. Forças e fraquezas são da organização; oportunidades e ameaças vêm de fora.</p>
            </div>
            <div className="mc-matrix" role="grid" aria-label="Matriz de Macro Cenário">
              <div className="mc-row mc-head" role="row">
                <span className="mc-corner" role="columnheader" aria-label="Quadrante" />
                {MACRO_DIMENSIONS.map(([d]) => {
                  const DI = DIM_ICON[d];
                  return <span key={d} className="mc-dim" role="columnheader" title={MACRO_DIMENSION_META[d].desc}><DI />{MACRO_DIMENSION_META[d].plural}</span>;
                })}
              </div>
              {MACRO_QUADRANTS.map(([q]) => (
                <div key={q} className={`mc-row q-${q}`} role="row">
                  <span className="mc-q" role="rowheader" title={MACRO_QUADRANT_META[q].desc}>
                    <b>{MACRO_QUADRANT_META[q].label}</b><i className="mc-sym">{MACRO_QUADRANT_META[q].symbol}</i>
                  </span>
                  {MACRO_DIMENSIONS.map(([d]) => (
                    <Cell key={d} q={q} d={d} factors={macroCell(c, q, d)} sel={sel} onSelect={select}
                      onAdd={text => add(q, d, text)} onPatch={patch} onDone={() => dropBlank()} />
                  ))}
                </div>
              ))}
            </div>
            <p className="in-foot mc-foot">
              <span>{m.total} {m.total === 1 ? 'fator' : 'fatores'}</span><span>{m.covered} de 4 quadrantes</span><span>{m.dimensions} de 6 dimensões</span>
              <span className="mc-foot-n">{macroReady(c) ? 'A síntese consolidada se atualiza a cada mudança.' : 'A síntese aparece com quatro fatores, em dois quadrantes ou mais.'}</span>
            </p>
          </section>

          <aside className="in-side mc-side">
            <SelectedPanel sel={sel} factor={selFactor} cellCount={sel ? macroCell(c, sel.q, sel.d).filter(f => f.text.trim()).length : 0}
              full={sel ? cellFull(sel.q, sel.d) : false} fullAt={cellFull}
              onMove={move} onText={text => (selFactor ? patch(selFactor.id!, { text }) : sel && text.trim() && add(sel.q, sel.d, text, { focus: false }))}
              onNew={() => sel && (dropBlank(), setSel({ q: sel.q, d: sel.d }), requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>(`[data-cell="${sel.q}:${sel.d}"]`)?.focus()))}
              onRemove={() => selFactor && remove(selFactor.id!)} />
            <section className="card mc-sug">
              <div className="gd-h"><h3 className="in-h3"><Ic.Sparkle />Sugestão da IA</h3>
                {sugBusy ? <span className="obj-st busy"><span className="spin" />Gerando</span> : null}</div>
              {!sel ? <p className="gd-empty">Selecione uma célula da matriz para ver as sugestões da IA para ela.</p>
                : !sug ? (sugBusy ? <div className="obj-skel"><i /><i /><i /></div> : <p className="gd-empty">As sugestões ainda não chegaram.</p>)
                : (() => {
                  const list = macroSuggestionsFor(sug, c, sel.q, sel.d);
                  const full = cellFull(sel.q, sel.d);
                  return list.length ? (
                    <ul className={`mc-sug-l${sugBusy ? ' is-dim' : ''}`}>
                      {list.map(text => (
                        <li key={text}>
                          <button type="button" onClick={() => add(sel.q, sel.d, text, { ai: true })} disabled={full}
                            title={full ? `A célula já tem ${MACRO_PER_CELL} fatores` : 'Adicionar à célula'}>
                            <span className="mc-plus"><Ic.Plus /></span><span>{text}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="gd-empty">As sugestões desta célula já estão na matriz. Peça outras.</p>;
                })()}
              <div className="mc-sug-f">
                <span>{sel ? `${MACRO_QUADRANT_META[sel.q].label} · ${MACRO_DIMENSION_META[sel.d].plural}` : ''}</span>
                <button type="button" className="btn-ghost sm" onClick={() => refresh.mutate()} disabled={sugBusy}><Ic.Refresh />Outras sugestões</button>
              </div>
              {sugQ.data?.note && <p className="gd-note"><Ic.Warning />{sugQ.data.note}</p>}
            </section>
          </aside>
        </div>
      ) : (
        <div className="mc-grid">
          <section className="card mc-card">
            <div className="gd-h mc-h">
              <h3 className="in-h3">Síntese consolidada do macro cenário</h3>
              <ReadingStatus updating={busy} basic={r?.basic ?? false} at={r?.updatedAt ?? null} />
            </div>
            {!syn ? (
              busy ? <div className="obj-skel"><i /><i /><i /><i /></div>
                : <p className="gd-empty">A síntese aparece quando a matriz tiver ao menos quatro fatores, em dois quadrantes ou mais. <button type="button" className="addlink mc-back" onClick={() => go('matriz')}><Ic.ChevronLeft />Voltar para a matriz</button></p>
            ) : (
              <div className={busy ? 'is-dim' : ''}>
                <div className="tablewrap nomask">
                  <table className="dense mc-syn">
                    <thead><tr><th>Quadrante</th><th>Principais fatores</th><th>Dimensões predominantes</th><th>Impacto estratégico</th></tr></thead>
                    <tbody>
                      {m.quadrants.map(q => {
                        const line = syn.quadrantes[q.key];
                        return (
                          <tr key={q.key} className={`q-${q.key}`}>
                            <td className="mc-syn-q"><span className="mc-q"><b>{q.label}</b><i className="mc-sym">{MACRO_QUADRANT_META[q.key].symbol}</i></span></td>
                            <td data-label="Principais fatores">{line.fatores || <span className="empty">Sem fatores nesta linha da matriz.</span>}</td>
                            <td data-label="Dimensões predominantes">
                              {q.dims.length ? <span className="mc-chips">{q.dims.slice(0, 3).map(x => <span key={x.key} className={`mc-chip d-${x.key}`}>{x.label}</span>)}</span> : <span className="empty">—</span>}
                            </td>
                            <td data-label="Impacto estratégico">{line.impacto || <span className="empty">—</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mc-dir">
                  <h4>Direcionamentos sugeridos</h4>
                  <ol className="bmc-nl">{syn.direcionamentos.map((x, i) => <li key={i}><span className="mono">{i + 1}</span>{x}</li>)}</ol>
                </div>
              </div>
            )}
            <ul className="mc-checks" aria-label="Situação da leitura">
              <Check ok={m.internal && m.external} label="Leitura interna e externa concluída" />
              <Check ok={m.covered === 4} label="Fatores nos quatro quadrantes" />
              <Check ok={!!syn && !busy} label="Insights prontos para a próxima etapa" />
            </ul>
            {r?.note && <p className="gd-note"><Ic.Warning />{r.note}</p>}
          </section>

          <aside className="in-side mc-side">
            <section className="card">
              <h3 className="in-h3">Validação final</h3>
              {syn ? (
                <div className={`mc-val${busy ? ' is-dim' : ''}`}>
                  <Ring pct={syn.validacao.score} />
                  <ul className="mc-crit">
                    {MACRO_CRITERIA.map(([k, label]) => (
                      <li key={k}><span>{label}</span><b>{syn.validacao.criterios[k]}%</b><span className="tr"><i style={{ '--v': `${syn.validacao.criterios[k]}%` } as CSSProperties} /></span></li>
                    ))}
                  </ul>
                </div>
              ) : busy ? <div className="obj-skel"><i /><i /></div> : <p className="gd-empty">A validação chega com a síntese.</p>}
            </section>
            {syn && (
              <section className={`card mc-status t-${syn.validacao.score >= 80 ? 'ok' : syn.validacao.score >= 60 ? 'mid' : 'low'}${busy ? ' is-dim' : ''}`}>
                <span className="mc-status-i">{syn.validacao.score >= 60 ? <Ic.Check /> : <Ic.Warning />}</span>
                <div><b>{syn.validacao.titulo}</b>{syn.validacao.texto && <p>{syn.validacao.texto}</p>}</div>
              </section>
            )}
            <section className="card mc-next">
              <h3 className="in-h3">Próximo passo</h3>
              <button type="button" className="mc-next-b" onClick={finish} disabled={consolidate.isPending}>
                <Ic.ArrowRight /><span>Avançar para a ferramenta 09 · 3 Horizontes de Inovação</span>
              </button>
              <p>Na próxima etapa, as oportunidades e as ameaças do macro cenário viram iniciativas em três horizontes de tempo — do que melhora hoje ao que transforma o futuro.</p>
            </section>
          </aside>
        </div>
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => { dropBlank(); flush(); nav('/strategy'); }}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => (tab === 'sintese' ? go('matriz') : (dropBlank(), flush(), nav('/strategy/inovacao')))}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending} onClick={() => (tab === 'matriz' ? go('sintese') : finish())}>
          {consolidate.isPending ? 'Consolidando o DNA…' : tab === 'sintese' ? 'Concluir ferramenta' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}

/* ── peças ────────────────────────────────────────────────────────── */

/** Uma célula da matriz: os fatores (campos) e, vazia ou selecionada, a linha para escrever mais um. */
function Cell({ q, d, factors, sel, onSelect, onAdd, onPatch, onDone }: {
  q: MacroQuadrant; d: MacroDimension; factors: MacroFactor[]; sel: Sel | null;
  onSelect: (s: Sel) => void; onAdd: (text: string) => void; onPatch: (id: string, p: Partial<MacroFactor>) => void; onDone: () => void;
}) {
  const on = sel?.q === q && sel.d === d;
  const ghost = factors.length < MACRO_PER_CELL && (!factors.length || (on && !sel?.id));
  const label = `${MACRO_QUADRANT_META[q].label} · ${MACRO_DIMENSION_META[d].label}`;
  const keys = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') { e.preventDefault(); e.currentTarget.blur(); onDone(); }
  };
  return (
    <div role="gridcell" className={`mc-cell${on ? ' on' : ''}${factors.length ? '' : ' empty'}`}
      onClick={e => {
        if (e.target !== e.currentTarget) return;
        const cell = e.currentTarget;
        onSelect({ q, d });
        /* a linha em branco, se couber mais um; senão, o último fator */
        requestAnimationFrame(() => (cell.querySelector<HTMLTextAreaElement>('[data-cell]') ?? [...cell.querySelectorAll<HTMLTextAreaElement>('textarea')].at(-1))?.focus());
      }}>
      <span className="mc-cell-l" aria-hidden="true">{MACRO_DIMENSION_META[d].plural}</span>
      {factors.map(f => (
        <div key={f.id} className={`mc-f${f.ai ? ' ai' : ''}${sel?.id === f.id ? ' sel' : ''}`}>
          {f.ai && <span className="in-ai" title="Sugerido pela IA — edite à vontade"><Ic.Sparkle /></span>}
          <textarea data-mf={f.id} className="mc-ta" rows={1} maxLength={1000} value={f.text} aria-label={`Fator — ${label}`}
            onFocus={() => onSelect({ q, d, id: f.id })} onChange={e => onPatch(f.id!, { text: e.target.value })} onKeyDown={keys} />
        </div>
      ))}
      {ghost && (
        <textarea data-cell={`${q}:${d}`} className="mc-ta ghost" rows={1} maxLength={1000} value="" aria-label={`Novo fator — ${label}`}
          placeholder={factors.length ? 'Mais um fator…' : 'Clique para adicionar um fator…'}
          onFocus={() => onSelect({ q, d })} onChange={e => e.target.value.trim() && onAdd(e.target.value)} onKeyDown={keys} />
      )}
      {!ghost && factors.length < MACRO_PER_CELL && (
        <button type="button" className="mc-more" aria-label={`Mais um fator em ${label}`}
          onClick={() => { onSelect({ q, d }); requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>(`[data-cell="${q}:${d}"]`)?.focus()); }}><Ic.Plus /></button>
      )}
    </div>
  );
}

function SelectedPanel({ sel, factor, cellCount, full, fullAt, onMove, onText, onNew, onRemove }: {
  sel: Sel | null; factor: MacroFactor | null; cellCount: number; full: boolean; fullAt: (q: MacroQuadrant, d: MacroDimension) => boolean;
  onMove: (p: { q?: MacroQuadrant; d?: MacroDimension }) => void; onText: (t: string) => void; onNew: () => void; onRemove: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { setConfirm(false); }, [factor?.id]);
  useEffect(() => { if (!confirm) return; const t = setTimeout(() => setConfirm(false), 3500); return () => clearTimeout(t); }, [confirm]);
  if (!sel) {
    return (
      <section className="card mc-selp">
        <h3 className="in-h3">Célula selecionada</h3>
        <p className="gd-empty">Clique numa célula da matriz para escrever um fator, movê-lo de quadrante ou de dimensão e ver as sugestões da IA.</p>
      </section>
    );
  }
  const blocked = (q: MacroQuadrant, d: MacroDimension) => !!factor && !(q === factor.quadrant && d === factor.dimension) && fullAt(q, d);
  return (
    <section className={`card mc-selp q-${sel.q}`}>
      <h3 className="in-h3">{factor ? 'Fator selecionado' : 'Célula selecionada'}</h3>
      <label className="mc-fld"><span>Quadrante</span>
        <span className="in-sel mc-qsel">
          <i className="mc-sym">{MACRO_QUADRANT_META[sel.q].symbol}</i>
          <select value={sel.q} onChange={e => onMove({ q: e.target.value as MacroQuadrant })}>
            {MACRO_QUADRANTS.map(([q]) => <option key={q} value={q} disabled={blocked(q, sel.d)}>{MACRO_QUADRANT_META[q].label}</option>)}
          </select><Ic.Caret />
        </span>
      </label>
      <label className="mc-fld"><span>Dimensão</span>
        <span className="in-sel">
          <select value={sel.d} onChange={e => onMove({ d: e.target.value as MacroDimension })}>
            {MACRO_DIMENSIONS.map(([d]) => <option key={d} value={d} disabled={blocked(sel.q, d)}>{MACRO_DIMENSION_META[d].plural}</option>)}
          </select><Ic.Caret />
        </span>
      </label>
      <label className="mc-fld mc-desc"><span>Descreva o fator</span>
        <textarea className="mc-big" maxLength={1000} rows={4} value={factor?.text ?? ''} disabled={!factor && full}
          placeholder={full && !factor ? `Esta célula já tem ${MACRO_PER_CELL} fatores.` : 'Um fator por vez: o que acontece e por que importa.'}
          onChange={e => onText(e.target.value)} />
        <small className="mono">{(factor?.text ?? '').length}/1.000</small>
      </label>
      <div className="mc-selp-a">
        <span className="mc-selp-n">{cellCount ? `${cellCount} de ${MACRO_PER_CELL} nesta célula` : 'Célula vazia'}</span>
        {factor && !full && <button type="button" className="btn-ghost sm" onClick={onNew}><Ic.Plus />Novo fator</button>}
        {factor && (confirm
          ? <button type="button" className="g-del" onClick={onRemove}>Excluir</button>
          : <button type="button" className="btn-ghost sm" onClick={() => setConfirm(true)}><Ic.Close />Remover</button>)}
      </div>
    </section>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return <li className={ok ? 'ok' : ''}><span className="mc-ck">{ok ? <Ic.Check /> : null}</span>{label}</li>;
}

function Ring({ pct }: { pct: number }) {
  const r = 34, len = 2 * Math.PI * r;
  return (
    <div className="bmc-ring mc-ring">
      <svg viewBox="0 0 80 80" aria-hidden="true">
        <circle cx="40" cy="40" r={r} className="bg" />
        <circle cx="40" cy="40" r={r} className="fg" strokeDasharray={`${(pct / 100) * len} ${len}`} transform="rotate(-90 40 40)" />
      </svg>
      <span><b>{pct}</b><small>de 100</small></span>
    </div>
  );
}
